import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Tooltip } from "../Tooltip";
import styles from "./ActivationChannels.module.css";
// import styles from "./Activation.module.css";

import SimpleBack from "../../assets/simpleBackIcon.svg";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { useAsyncFn } from "../../hooks/use-async-fn";
import InfoPopup from "../InfoPopup";
import DV360Icon from "../../assets/dv360.svg";
import PubMaticIcon from "../../assets/pubMatic.png";
import GoogleAdsIcon from "../../assets/googleads.png";
import MetaIcon from "../../assets/meta.svg";
import AWSIcon from "../../assets/aws.svg";
import TTDIcon from "../../assets/ttd.svg";
import AzureIcon from "../../assets/azure.png";
import MediasmartIcon from "../../assets/mediasmart.png";
import GCPIcon from "../../assets/gcp.png";
import SFTPIcon from "../../assets/sftp.jpg";
import ExpandIcon from "../../assets/ExpandIcon";
import DeleteIcon from "../../assets/Stop.svg";
import CloudIcon from "../../assets/cloud.png";
import PageNotFound from "../PageNotFound";
import config from "../../config";
import { getCSSVariable } from "../../utils";
import InforIcon from "../../assets/InfoCircleIcon";
import LayoutContainer from "../LayoutContainer";
import SearchableDropdown from "../SearchableDropdown";

interface ActivationChannelsProps {
  authContext: any;
}

const channels = [
  {
    id: "dv360",
    name: "DV360",
    url: "Display & Video 360",
    status: "Ready",
    icon: DV360Icon,
  },
  {
    id: "pubmatic",
    name: "PubMatic",
    url: "PubMatic",
    status: "Ready",
    icon: PubMaticIcon,
  },
  {
    id: "googleads",
    name: "Google Ads",
    url: "Google Ads",
    status: "Ready",
    icon: GoogleAdsIcon,
  },
  {
    id: "ttd",
    name: "The Trade Desk",
    url: "The Trade Desk",
    status: "Ready",
    icon: TTDIcon,
  },
  {
    id: "meta",
    name: "Meta Ad Server",
    url: "Meta",
    status: "Ready",
    icon: MetaIcon,
  },
  {
    id: "aws",
    name: "AWS",
    url: "AWS S3 Bucket",
    status: "Ready",
    icon: AWSIcon,
  },
  {
    id: "azure",
    name: "Azure",
    url: "Azure Blob Container",
    status: "Ready",
    icon: AzureIcon,
  },
  {
    id: "mediasmart",
    name: "Mediasmart",
    url: "Mediasmart",
    status: "Ready",
    icon: MediasmartIcon,
  },
  {
    id: "gcp",
    name: "GCP",
    url: "Google Cloud Storage",
    status: "Ready",
    icon: GCPIcon,
  },
  {
    id: "sftp",
    name: "SFTP",
    url: "SFTP",
    status: "Ready",
    icon: SFTPIcon,
  },
];

const fieldMapping = {
  aws: [
    {
      key: "destinationLocation",
      label: "Destination Credentials (AWS Secret Managed)",
      mandatory: true,
    },
    { key: "bucketName", label: "Bucket Name", mandatory: true },
    { key: "bucketRegion", label: "Bucket Region", mandatory: true },
    { key: "name", label: "Name", mandatory: true },
  ],
  dv360: [
    { key: "creds", label: "Service Account ID", mandatory: true },
    { key: "advertiserId", label: "Advertiser ID", mandatory: true },
    { key: "seatId", label: "Seat ID", mandatory: true },
    { key: "name", label: "Name", mandatory: true },
    { key: "appId", label: "App ID", mandatory: false },
  ],
  pubmatic: [
    { key: "name", label: "Name", mandatory: true },
    { key: "secretName", label: "Secret Name", mandatory: true },
    { key: "dpid", label: "DPID", mandatory: true },
  ],
  googleads: [
    { key: "name", label: "Name", mandatory: true },
    { key: "customerId", label: "Customer ID", mandatory: true },
    {
      key: "credsType",
      label: "Credentials Type",
      mandatory: true,
      type: "select",
      options: [
        { value: "service_account_key", label: "Service Account ID" },
        {
          value: "impersonated_service_account",
          label: "Impersonated Service Account",
        },
      ],
    },
    { key: "credsSecret", label: "Secret Credentials/Service Account ID", mandatory: true },
    {
      key: "loginCustomerId",
      label: "Login Customer ID",
      mandatory: false,
    },
    {
      key: "appId",
      label: "App ID",
      mandatory: false,
      hint: "Mobile ID/Device ID flow",
    },
    {
      key: "keySpace",
      label: "Key Space",
      mandatory: false,
      type: "select",
      options: [
        { value: "ANDROID", label: "ANDROID" },
        { value: "IOS", label: "IOS" },
      ],
      hint: "Mobile ID/Device ID flow. Must be IOS or ANDROID if set.",
    },
  ],
  ttd: [
    {
      key: "activationFlow",
      label: "Audience Match Type",
      mandatory: false,
      type: "select",
      options: [
        { value: "daid", label: "Device ID (DAID)" },
        { value: "hashed", label: "Hashed Email/Phone" },
      ],
      hint: "Choose how audiences are matched for this connection.",
    },
    { key: "name", label: "Name", mandatory: true },
    { key: "advertiserId", label: "Advertiser ID", mandatory: true },
    {
      key: "advertiserSecretKey",
      label: "Advertiser Secret Key",
      mandatory: true,
    },
    { key: "dataProviderId", label: "Data Provider ID", mandatory: true },
    {
      key: "dataProviderSecretKey",
      label: "Data Provider Secret Key",
      mandatory: true,
    },
    {
      key: "token",
      label: "Token",
      mandatory: true,
      hint: "Hashed Email/Phone match type only. Generate from TTD Partner Portal → Manage API Tokens.",
    },
  ],
  meta: [
    { key: "clientId", label: "App ID", mandatory: true },
    { key: "clientSecret", label: "App Secret", mandatory: true },
    { key: "adAccountId", label: "Ad Account ID", mandatory: true },
    { key: "fbExchangeToken", label: "FB Exchange Token", mandatory: true },
    { key: "name", label: "Name", mandatory: true },
  ],
  azure: [
    { key: "name", label: "Name", mandatory: true },
    {
      key: "secretNamePrefixDst",
      label: "Destination Credentials Secret Name",
      mandatory: true,
    },
    { key: "containerName", label: "Container Name", mandatory: true },
  ],
  mediasmart: [
    { key: "name", label: "Name", mandatory: true },
    { key: "password", label: "Password", mandatory: true },
    {
      key: "mediasmartOrganizationId",
      label: "Mediasmart Organization ID",
      mandatory: true,
    },
  ],
  gcp: [
    {
      key: "destinationLocation",
      label: "Destination Credentials (AWS Secret Managed)",
      mandatory: true,
    },
    { key: "bucketName", label: "Bucket Name", mandatory: true },
    { key: "resultLocation", label: "Result Location", mandatory: true },
    { key: "name", label: "Name", mandatory: true },
  ],
  sftp: [
    {
      key: "destinationLocation",
      label: "Destination Credentials (AWS Secret Managed)",
      mandatory: true,
    },
    { key: "bucketName", label: "Bucket Name", mandatory: true },
    { key: "bucketRegion", label: "Bucket Region", mandatory: true },
    { key: "name", label: "Name", mandatory: true },
  ],
};

const ActivationChannels: React.FC<ActivationChannelsProps> = ({
  authContext,
}) => {
  const fedid =
    localStorage.getItem("federationkey") === "null"
      ? null
      : localStorage.getItem("federationkey");
  const userRole = localStorage.getItem("userRole");
  const isPlatformOrAppAdmin =
    userRole === config.superUserRole ||
    userRole === config.applicationAdminRole;
  const fedSpecificMenuLabels = JSON.parse(
    localStorage.getItem("fedSpecificMenuLabels") || "{}",
  );
  const {
    getorganizationById,
    getAdserversByOrgId,
    deleteAdserverByOrgId,
    getApplication,
    getOrganizations,
  } = authContext;
  const location = useLocation<{
    activeTab?: "adservers" | "cloud";
  }>();

  const cloudChannels = ["gcp", "aws", "azure", "sftp"];
  const history = useHistory();

  // ── existing logic state (unchanged) ──────────────────────────────────────
  const [activeTab, setActiveTab] = useState<"adservers" | "cloud">(
    location.state?.activeTab ?? "adservers",
  );
  const [existingConnectionsTab, setExistingConnectionsTab] = useState<
    "adservers" | "cloud"
  >("adservers");
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [mode] = useState<"add" | "delete">("add");
  const { setBreadcrumbItems } = useBreadcrumb();
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();
  const [dynamicChannels, setDynamicChannels] = useState(channels);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  var organizationId = localStorage.getItem("organizationkey");
  const [primaryColorCode, setPrimaryColorCode] = useState("");
  const [, setElementColorCode] = useState("");
  const [deletingConnector, setDeletingConnector] = useState(null);
  const [deletePopupConnector, setDeletePopupConnector] = useState(null);
  const [expandedConnectorOrg, setExpandedConnectorOrg] = useState("");
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const storedPermissions = JSON.parse(localStorage.getItem("userPermissions"));
  const readOnly = storedPermissions?.activationconnectors === "read";
  const getApplicationFn = useAsyncFn({
    promiseFn: getApplication,
  });
  const [selectedOrg, setSelectedOrg] = useState<string>("");

  const [selectedOrgName, setSelectedOrgName] = useState<string>("");
  const deleteAdserverByOrgIdFn = useAsyncFn({
    deferFn: deleteAdserverByOrgId,
  });

  const [expandedData, setExpandedData] = useState({});
  const [expandedName, setExpandedName] = useState("");
  const [selectedConnector, setSelectedConnector] = useState(null);

  const [showPopup, setShowPopup] = useState(false);

  const getorganizationByIdFn = useAsyncFn({
    deferFn: getorganizationById,
  });

  const getAdserversByOrgIdFn = useAsyncFn({
    deferFn: getAdserversByOrgId,
  });

  // ── new UI-only state ──────────────────────────────────────────────────────
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);
  const [modalTab, setModalTab] = useState<"adservers" | "cloud">("adservers");
  const [typeFilter, setTypeFilter] = useState<"all" | "adservers" | "cloud">(
    "all",
  );

  // ── existing effects (unchanged) ───────────────────────────────────────────
  useEffect(() => {
    if (isPlatformOrAppAdmin && selectedOrg) {
      setActivationData([]);
      getAdserversByOrgIdFn
        .run({ organizationId: selectedOrg })
        .then((data: any) => {
          setActivationData(data?.ad_server ?? []);
        })
        .catch((err) => {
          console.error("Handled gracefully:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, isPlatformOrAppAdmin]);

  useEffect(() => {
    if (!isPlatformOrAppAdmin) {
      getorganizationByIdFn
        .run({
          organizationID: organizationId,
        })
        .catch((err) => {
          console.error("Handled gracefully:", err);
        });
      getAdserversByOrgIdFn
        .run({ organizationId })
        .then((data: any) => {
          setActivationData(data?.ad_server ?? []);
        })
        .catch((err) => {
          console.error("Handled gracefully:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const organizationData =
    getorganizationByIdFn.data && getorganizationByIdFn.data;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [activationData, setActivationData] = useState([]);

  const adServersData = (activationData as any[]).filter(
    (c) => c.ad_server && c.ad_server !== "",
  );
  const cloudData = (activationData as any[]).filter(
    (c) => c.cloud && c.cloud !== "",
  );

  // original computed value (kept intact)
  const filteredExistingData =
    existingConnectionsTab === "adservers" ? adServersData : cloudData;

  // new computed value for filter pills
  const filteredByPill =
    typeFilter === "adservers"
      ? adServersData
      : typeFilter === "cloud"
        ? cloudData
        : (activationData as any[]);

  useEffect(() => {
    if (!activationData?.length) return;
    const hasAdServers = (activationData as any[]).some(
      (c) => c.ad_server && c.ad_server !== "",
    );
    setExistingConnectionsTab(hasAdServers ? "adservers" : "cloud");
  }, [activationData]);

  const closePopup = () => {
    setSelectedConnector(null);
  };

  const handleDelete = async (name, callerOrgId = "") => {
    setDeletingConnector(name);

    const connector = (activationData as any[]).find((c) => c.name === name);
    const targetOrgId = isPlatformOrAppAdmin
      ? callerOrgId || selectedOrg
      : organizationId;

    try {
      await deleteAdserverByOrgIdFn.run({
        organizationId: targetOrgId,
        uuids: [connector?.uuid],
      });
      setActivationData((prev: any[]) => prev.filter((c) => c.name !== name));
    } catch (error) {
      console.error(error.message);
    } finally {
      setDeletingConnector(null);
    }
  };

  const onExpand = (cloudName) => {
    const entry = activationData?.find((item) => item.name === cloudName);

    if (!entry) {
      console.error("No data found for cloud:", cloudName);
      return;
    }

    setExpandedConnectorOrg(entry.orgName);
    setSelectedConnector(entry);
    setExpandedName(entry.name);

    if (
      entry.cloud === "aws" ||
      entry.cloud === "azure" ||
      entry.cloud === "gcp" ||
      entry.cloud === "sftp"
    ) {
      let cloudMetadata = entry.cloud_connection_metadata;

      if (entry.cloud === "sftp") {
        const { result_location, ...rest } = cloudMetadata;
        cloudMetadata = rest;
      }

      setExpandedData(cloudMetadata);
    } else {
      setExpandedData(entry.adserver_connection_metadata);
    }

    setShowPopup(true);
  };

  const handleCardClick = (channelId: string) => {
    setSelectedChannels((prev) => {
      let updated;
      if (mode === "add") {
        updated = prev.includes(channelId) ? [] : [channelId];
      } else {
        updated = prev.includes(channelId)
          ? prev.filter((id) => id !== channelId)
          : [...prev, channelId];
      }

      if (mode === "delete") {
        setShowDeletePopup(true);
      } else {
        const filteredFields = channels
          .filter((channel) => updated.includes(channel.id))
          .map((channel) => ({
            id: channel.id,
            name: channel.name,
            fields: fieldMapping[channel.id] || [],
          }));

        history.push("/activation-form", {
          selectedFields: filteredFields,
          organizationData,
          existingConnectors: activationData,
          selectedOrg: selectedOrg,
          selectedOrgName: selectedOrgName,
          activeTab,
        });
      }

      return updated;
    });
  };

  useEffect(() => {
    if (!activationData) return;

    const readyChannels = activationData?.map(
      (item: any) => item.ad_server || item.cloud,
    );

    setDynamicChannels(
      channels.map((channel) => ({
        ...channel,
        status: readyChannels.includes(channel.id) ? "Ready" : "",
      })),
    );
  }, [activationData]);

  useEffect(() => {
    const applicationData = getApplicationFn.data && getApplicationFn.data;

    if (
      getApplicationFn.data &&
      applicationData &&
      applicationData.color_scheme
    ) {
      setPrimaryColorCode(getCSSVariable("--active-bg-color"));
      setElementColorCode(applicationData.color_scheme.element_color);
    }
  }, [getApplicationFn.data]);

  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      {
        label:
          fedSpecificMenuLabels?.activation_connectors || "Activation Channels",
        path: "/activation-channels",
      },
    ]);
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/activation-channels")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);
    setIsSideBarMenuDisabled({
      fed: false,
      cp: fedid === null ? true : false,
      cr: fedid === null ? true : false,
      pr: fedid === null ? true : false,
      ca: false,
      ah: fedid === null ? true : false,
      dm: fedid === null ? true : false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // console.log("activationData", activationData);

  // suppress unused-variable warning; primaryColorCode kept for logic parity
  void primaryColorCode;
  void filteredExistingData;
  void setActiveTab;

  return storedPermissions?.activationconnectors === "no" ? (
    <PageNotFound />
  ) : (
    <LayoutContainer>
      <>
        <div className={styles.actionsBar}>
          <button
            className={styles.backButton}
            onClick={() => history.push("/federation-selection")}
          >
            <img src={SimpleBack} alt="back" />
            <span className={styles.backButtonText}>Back</span>
          </button>
          <InforIcon
            className={styles.infoIcon}
            onClick={() => setShowInfoPopup(true)}
          />
          {showInfoPopup && (
            <InfoPopup
              displayText={
                "Configure connectors for your activation channels. Add single or multiple connectors per channel, or remove any that are no longer needed."
              }
              isOpen={showInfoPopup}
              onClose={() => setShowInfoPopup(false)}
              closeButtonLabel="Got it"
              headerText="Information"
              freeText
            />
          )}
        </div>

        <div className={styles.container}>
          {showDeletePopup && (
            <InfoPopup
              isOpen={showDeletePopup}
              onClose={() => setShowDeletePopup(false)}
              headerText="Delete Channels"
              displayText={`Are you sure you want to delete these channels? "${channels
                .filter((channel) => selectedChannels.includes(channel.id))
                .map((channel) => channel.name)
                .join(", ")}"`}
              showCancelButton={true}
              onCancelButtonClick={() => setShowDeletePopup(false)}
              freeText={true}
              closeButtonLabel={"Delete"}
            />
          )}

          <div className={styles.bigContainer}>
            <div className={styles.scrollableContent}>
              {/* Read-only empty state */}
              {!isPlatformOrAppAdmin && !activationData?.length && readOnly && (
                <h3 className={styles.emptyMsg}>
                  <i>
                    No activation channels found. Please contact the
                    administrator to set up a new connector.
                  </i>
                </h3>
              )}

              {/* Main management section */}
              {(isPlatformOrAppAdmin ||
                activationData?.length > 0 ||
                !readOnly) && (
                <div className={styles.existingConnectionsContainer}>
                  {/* Section header */}
                  <div className={styles.sectionHeader}>
                    <h3>
                      Existing Connections
                      {activationData?.length > 0 && (
                        <span className={styles.countBadge}>
                          {activationData.length}
                        </span>
                      )}
                    </h3>
                    <div className={styles.sectionHeaderActions}>
                      {isPlatformOrAppAdmin && (
                        <SearchableDropdown<{ id: string; name: string }>
                          fetchOptions={(name) =>
                            getOrganizations({ args: { name, page_size: 8 } })
                          }
                          onSelect={(org) => {
                            setSelectedOrg(org.id);
                            setSelectedOrgName(org.name);
                          }}
                          onClear={() => {
                            setSelectedOrg("");
                            setSelectedOrgName("");
                          }}
                          getLabel={(org) => org.name}
                          getValue={(org) => org.id}
                          selectedValue={selectedOrg}
                          selectedLabel={selectedOrgName}
                          placeholder="Search organization…"
                          className={styles.orgSelect}
                        />
                      )}
                      {!readOnly && (
                        <button
                          className={styles.addConnectorBtn}
                          onClick={() => setAddConnectorOpen(true)}
                        >
                          + Add Connector
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Filter pills — only rendered when there is data to filter */}
                  {activationData?.length > 0 && (
                    <div className={styles.filterPillRow}>
                      <button
                        className={`${styles.filterPill} ${typeFilter === "all" ? styles.filterPillActive : ""}`}
                        onClick={() => setTypeFilter("all")}
                      >
                        All
                        <span className={styles.filterPillCount}>
                          {(activationData as any[]).length}
                        </span>
                      </button>
                      <button
                        className={`${styles.filterPill} ${typeFilter === "adservers" ? styles.filterPillActive : ""}`}
                        onClick={() => setTypeFilter("adservers")}
                      >
                        Ad Servers
                        <span className={styles.filterPillCount}>
                          {adServersData.length}
                        </span>
                      </button>
                      <button
                        className={`${styles.filterPill} ${typeFilter === "cloud" ? styles.filterPillActive : ""}`}
                        onClick={() => setTypeFilter("cloud")}
                      >
                        Cloud &amp; Storage
                        <span className={styles.filterPillCount}>
                          {cloudData.length}
                        </span>
                      </button>
                    </div>
                  )}

                  {/* Empty states — rendered outside the table so headers don't show on blank pages */}
                  {isPlatformOrAppAdmin && !selectedOrg ? (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyStateIconRing}>
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </div>
                      <p className={styles.emptyStateTitle}>
                        Select an organization
                      </p>
                      <p className={styles.emptyStateSubtext}>
                        Search for an organization above to view and manage its
                        activation connectors.
                      </p>
                    </div>
                  ) : filteredByPill.length === 0 ? (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyStateIconRing}>
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <rect
                            x="2"
                            y="7"
                            width="20"
                            height="14"
                            rx="2"
                            ry="2"
                          />
                          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                        </svg>
                      </div>
                      <p className={styles.emptyStateTitle}>
                        No connectors configured
                      </p>
                      <p className={styles.emptyStateSubtext}>
                        {readOnly
                          ? "No connectors have been set up yet. Contact your administrator."
                          : 'Click "+ Add Connector" above to configure your first activation channel.'}
                      </p>
                    </div>
                  ) : (
                    <table className={styles.existingConnectionsTable}>
                      <thead>
                        <tr>
                          <th className={styles.tableTh}>#</th>
                          <th className={styles.tableTh}>Connector Name</th>
                          <th className={styles.tableTh}>Channel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredByPill
                          .slice()
                          .sort((a, b) =>
                            (a.name || "").localeCompare(b.name || ""),
                          )
                          .map((connector, index) => {
                            const channelVal =
                              connector.cloud || connector.ad_server;
                            const isCloud = !!connector.cloud;
                            const channelIcon = channels.find(
                              (c) => c.id === channelVal?.toLowerCase(),
                            )?.icon;
                            const channelLabel =
                              channels.find(
                                (c) => c.id === channelVal?.toLowerCase(),
                              )?.name ?? channelVal;
                            return (
                              <tr key={index}>
                                <td className={styles.connectorIndexCell}>
                                  {index + 1}
                                </td>
                                <td className={styles.nameCell}>
                                  <div className={styles.nameCellInner}>
                                    <Tooltip
                                      text={connector.name || "Unknown"}
                                      placement="top"
                                    >
                                      <span
                                        className={styles.connectorNameText}
                                      >
                                        {connector.name || "Unknown"}
                                      </span>
                                    </Tooltip>
                                    <div className={styles.actionsGroup}>
                                      <Tooltip
                                        text="View connector details"
                                        placement="top"
                                      >
                                        <button
                                          className={`${styles.actionIconBtn} ${styles.actionIconBtnPrimary}`}
                                          onClick={() =>
                                            onExpand(connector?.name)
                                          }
                                        >
                                          <ExpandIcon
                                            className={styles.icon}
                                            style={{ display: "block" }}
                                          />
                                        </button>
                                      </Tooltip>
                                      {!readOnly &&
                                        (deletingConnector ===
                                        connector.name ? (
                                          <div
                                            className={styles.spinnerContainer}
                                          >
                                            <div
                                              className={styles.spinner}
                                            ></div>
                                          </div>
                                        ) : (
                                          <Tooltip
                                            text="Delete this connection"
                                            placement="top"
                                          >
                                            <button
                                              className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
                                              onClick={() =>
                                                setDeletePopupConnector(
                                                  connector.name,
                                                )
                                              }
                                            >
                                              <img
                                                src={DeleteIcon}
                                                alt="Delete"
                                                className={styles.icon}
                                              />
                                            </button>
                                          </Tooltip>
                                        ))}
                                    </div>
                                  </div>
                                  {selectedConnector &&
                                    showPopup &&
                                    expandedData && (
                                      <div
                                        className={styles.popupOverlay}
                                        onClick={closePopup}
                                      >
                                        <div
                                          className={styles.popupContent}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div
                                            className={
                                              styles.connectorPopupHeader
                                            }
                                          >
                                            <h2
                                              className={
                                                styles.connectorHeading
                                              }
                                            >
                                              Connector Details
                                            </h2>
                                            <button
                                              className={
                                                styles.connectorPopupCloseBtn
                                              }
                                              onClick={closePopup}
                                              aria-label="Close"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                          <div
                                            className={
                                              styles.connectorPopupBody
                                            }
                                          >
                                            {expandedName && (
                                              <div className={styles.detailRow}>
                                                <strong>Name</strong>
                                                <span
                                                  className={
                                                    styles.connectorVal
                                                  }
                                                >
                                                  {String(expandedName)}
                                                </span>
                                              </div>
                                            )}
                                            {Object.entries(expandedData)
                                              .filter(
                                                ([key, value]) =>
                                                  ![
                                                    "client_secret",
                                                    "fb_exchange_token",
                                                    "long_lived_access_token",
                                                    "advertiserSecretKey",
                                                    "dataProviderSecretKey",
                                                    "password",
                                                    "token",
                                                    "creds_secret",
                                                  ].includes(key) &&
                                                  value !== "" &&
                                                  value !== null &&
                                                  value !== undefined,
                                              )
                                              .map(([key, value]) => {
                                                const strVal = String(value);
                                                const isLong =
                                                  strVal.length > 40;
                                                return (
                                                  <div
                                                    key={key}
                                                    className={styles.detailRow}
                                                  >
                                                    <strong>
                                                      {key
                                                        .replace(/_/g, " ")
                                                        .replace(/\b\w/g, (c) =>
                                                          c.toUpperCase(),
                                                        )}
                                                    </strong>
                                                    <span
                                                      className={
                                                        isLong
                                                          ? styles.longVal
                                                          : styles.connectorVal
                                                      }
                                                    >
                                                      {strVal}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            <div className={styles.detailRow}>
                                              <strong>Organization Name</strong>
                                              <span
                                                className={styles.connectorVal}
                                              >
                                                {!isPlatformOrAppAdmin
                                                  ? getorganizationByIdFn.data
                                                      ?.name
                                                  : expandedConnectorOrg ||
                                                    selectedOrgName}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  {deletePopupConnector === connector.name && (
                                    <InfoPopup
                                      isOpen={
                                        deletePopupConnector === connector.name
                                      }
                                      title="Delete Connection"
                                      displayText={`Are you sure you want to delete "${connector?.name}"?`}
                                      showCancelButton={true}
                                      onCancelButtonClick={() =>
                                        setDeletePopupConnector(null)
                                      }
                                      freeText={true}
                                      closeButtonLabel="Delete"
                                      danger={true}
                                      onClose={() => {
                                        handleDelete(
                                          connector?.name,
                                          isPlatformOrAppAdmin
                                            ? selectedOrg
                                            : connector?.orgId || "",
                                        );
                                        setDeletePopupConnector(null);
                                      }}
                                    />
                                  )}
                                </td>
                                <td>
                                  <div className={styles.channelCellInner}>
                                    {channelIcon && (
                                      <img
                                        src={channelIcon}
                                        alt={channelLabel}
                                        className={`${styles.channelLogo} ${
                                          channelVal?.toLowerCase() === "pubmatic"
                                            ? styles.channelLogoWide
                                            : ""
                                        }`}
                                      />
                                    )}
                                    {channelVal && (
                                      <span
                                        className={`${styles.channelBadge} ${isCloud ? styles.cloudBadge : styles.adServerBadge}`}
                                      >
                                        {channelLabel}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Add Connector modal ──────────────────────────────────────────── */}
        {addConnectorOpen && !readOnly && (
          <div
            className={styles.addConnectorOverlay}
            onClick={() => setAddConnectorOpen(false)}
          >
            <div
              className={styles.addConnectorPanel}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.addConnectorHeader}>
                <h3 className={styles.addConnectorTitle}>Add Connector</h3>
                <button
                  className={styles.addConnectorCloseBtn}
                  onClick={() => setAddConnectorOpen(false)}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <div className={styles.addConnectorBody}>
                <p className={styles.gridHint}>
                  Click a channel card to configure a connector.
                </p>

                {/* Modal-level tabs */}
                <div className={styles.addConnectorTabRow}>
                  <button
                    className={`${styles.modalTab} ${modalTab === "adservers" ? styles.modalTabActive : ""}`}
                    onClick={() => setModalTab("adservers")}
                  >
                    Ad Servers
                  </button>
                  <button
                    className={`${styles.modalTab} ${modalTab === "cloud" ? styles.modalTabActive : ""}`}
                    onClick={() => setModalTab("cloud")}
                  >
                    Cloud &amp; Storage
                  </button>
                </div>

                {/* Channel cards grid */}
                <div className={styles.gridContainer}>
                  {dynamicChannels
                    .filter((channel) =>
                      modalTab === "cloud"
                        ? cloudChannels.includes(channel.id)
                        : !cloudChannels.includes(channel.id),
                    )
                    .map((channel) => (
                      <div
                        key={channel.id}
                        className={`${styles.channelBox} ${
                          selectedChannels.includes(channel.id)
                            ? styles.selectedBox
                            : ""
                        } ${
                          channel.status === "Ready"
                            ? styles.readyChannelBox
                            : ""
                        }`}
                        onClick={() => {
                          handleCardClick(channel.id);
                          setAddConnectorOpen(false);
                        }}
                      >
                        <div className={styles.hoverOverlay}>
                          <span className={styles.hoverOverlayLabel}>
                            + Configure
                          </span>
                        </div>
                        <div className={styles.iconWrapper}>
                          <Tooltip text={channel.id} placement="top">
                            <img
                              src={channel.icon}
                              alt={channel.name}
                              className={`${styles.logo} ${
                                channel.id === "pubmatic" ? styles.logoWide : ""
                              }`}
                            />
                          </Tooltip>
                        </div>
                        <div className={styles.channelInfo}>
                          <div className={styles.channelName}>
                            <span>{channel.name}</span>
                            {cloudChannels.includes(channel.id) && (
                              <img
                                src={CloudIcon}
                                className={styles.cloudIcon}
                                alt="cloud"
                              />
                            )}
                          </div>
                          <div className={styles.channelUrl}>{channel.url}</div>
                        </div>
                        <div
                          className={`${styles.statusTag} ${
                            channel.status === "Ready"
                              ? styles.readyTag
                              : styles.otherTag
                          }`}
                        >
                          {channel.status}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className={styles.addConnectorFooter}>
                <a
                  className={styles.setupText}
                  href="https://saptharushi.notion.site/Saptharushi-Data-Collaboration-Platform-285a16bad2dc80c4968cf9cd6b2ae824"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here to view your setup guide
                </a>
              </div>
            </div>
          </div>
        )}
      </>
    </LayoutContainer>
  );
};

export default ActivationChannels;
